import { useEffect, useState } from "react";
import { GitCommitHorizontal } from "lucide-react";
import { cn } from "@/lib/cn";

interface Metadata {
  version: string;
  commit: string;
  buildTime: string;
}

// BuildInfo is a quiet build-provenance stamp: fetches the metadata.json the
// Docker build stamps with the short git commit hash + build time (see
// web/scripts/gen-metadata.mjs, Dockerfile), so whoever is looking at a
// deployed environment can confirm exactly what's live without SSHing in or
// asking. In plain local dev, the checked-in placeholder metadata.json
// (commit "dev") is served as-is, so it reads as a local build rather than a
// real deploy.
//
// The pill is ALWAYS mounted at its final size, even before the fetch
// resolves (just invisible) - never conditionally unmounted - because it is
// the footer row of AuthShell's form column, whose content centers itself in
// the leftover space. Mounting this row late would shrink that space and
// visibly shift the form up once the fetch resolves; reserving the row from
// first paint and only fading its content in avoids that entirely.
export function BuildInfo({ className }: { className?: string }) {
  const [meta, setMeta] = useState<Metadata | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/metadata.json")
      .then((res) => (res.ok ? (res.json() as Promise<Metadata>) : null))
      .then((data) => {
        if (!cancelled && data) setMeta(data);
      })
      .catch(() => {
        // No metadata.json (e.g. a stripped-down environment) - stay blank.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isDev = meta?.commit === "dev";
  const builtAt = meta && !isDev && meta.buildTime ? new Date(meta.buildTime) : null;
  // House date style is MM/DD/YY (org convention), plus a short time.
  const builtLabel = builtAt
    ? builtAt.toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  return (
    <div
      title={meta ? (builtAt ? `Klutch ${meta.commit} · built ${builtLabel}` : "Local development build") : undefined}
      className={cn(
        "inline-flex items-center gap-1.5 text-[12px] text-muted-2 transition-opacity duration-300",
        meta ? "opacity-100" : "opacity-0",
        className,
      )}
      aria-hidden={!meta}
    >
      <GitCommitHorizontal size={13} className="shrink-0" />
      <span className="font-mono">{meta ? (isDev ? "dev" : meta.commit) : "0000000"}</span>
      {builtLabel && <span>· {builtLabel}</span>}
    </div>
  );
}
